import React, { useEffect, useState } from "react";
import { Route, Routes, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Building2, Users, MessageSquareText, Inbox, Plus, Trash2, ExternalLink, Copy, PenSquare } from "lucide-react";
import { DashboardShell, StatCard, SectionTitle, Btn, Input, Modal, EmptyState } from "@/components/DashUI";
import { LeadForms } from "@/pages/LeadFormsAdmin";

const NAV = [
  { to: "/admin", label: "Overview", icon: <LayoutDashboard size={16} /> },
  { to: "/admin/organizations", label: "Organizations", icon: <Building2 size={16} />, match: "/admin/organizations" },
  { to: "/admin/lead-forms", label: "Lead Forms", icon: <PenSquare size={16} />, match: "/admin/lead-forms" },
  { to: "/admin/users", label: "Users", icon: <Users size={16} /> },
  { to: "/admin/demo-requests", label: "Demo Requests", icon: <MessageSquareText size={16} /> },
];

export function SuperAdmin() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="organizations" element={<OrgList />} />
      <Route path="organizations/:orgId" element={<OrgDetail />} />
      <Route path="lead-forms/*" element={<LeadForms nav={NAV} />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="demo-requests" element={<DemoRequests />} />
    </Routes>
  );
}

/* ==================== Overview ==================== */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setStats(r.data)); }, []);
  return (
    <DashboardShell title="Super Admin" subtitle="Platform Overview" nav={NAV} testId="super-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Organizations" value={stats?.organizations} testId="stat-orgs" />
        <StatCard label="Active" value={stats?.active_organizations} testId="stat-active-orgs" />
        <StatCard label="Events" value={stats?.events} testId="stat-events" />
        <StatCard label="Conversations" value={stats?.conversations} testId="stat-conversations" />
        <StatCard label="Leads captured" value={stats?.leads} testId="stat-leads" />
        <StatCard label="Knowledge docs" value={stats?.kb_docs} testId="stat-kb" />
      </div>
      <div className="mt-10 border border-gray-200 rounded-xl p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Quick actions</div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to="/admin/organizations"><Btn variant="primary"><Plus size={14} /> Onboard organization</Btn></Link>
          <Link to="/admin/users"><Btn variant="secondary"><Users size={14} /> Manage users</Btn></Link>
          <Link to="/admin/demo-requests"><Btn variant="secondary"><Inbox size={14} /> Review demo requests</Btn></Link>
        </div>
      </div>
    </DashboardShell>
  );
}

/* ==================== Organizations list ==================== */
function OrgList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "", website: "", contact_email: "", brand_color: "#7C3AED", admin_email: "", admin_password: "", admin_full_name: "" });
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  async function refresh() {
    setLoading(true);
    const { data } = await api.get("/organizations");
    setRows(data);
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/organizations", form);
      toast.success("Organization created");
      setModal(false);
      setForm({ name: "", slug: "", description: "", website: "", contact_email: "", brand_color: "#7C3AED", admin_email: "", admin_password: "", admin_full_name: "" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm("Delete this organization and all its data? This cannot be undone.")) return;
    await api.delete(`/organizations/${id}`);
    toast.success("Organization deleted");
    refresh();
  }

  return (
    <DashboardShell title="Super Admin" subtitle="Organizations" nav={NAV}>
      <SectionTitle title="All organizations" action={<Btn onClick={() => setModal(true)} testId="new-org-btn"><Plus size={14} /> New organization</Btn>} />

      {loading ? <div className="text-sm text-gray-500">Loading…</div> :
       rows.length === 0 ? <EmptyState icon={<Building2 size={18} />} title="No organizations yet" body="Onboard your first customer." action={<Btn onClick={() => setModal(true)}><Plus size={14} /> New organization</Btn>} /> :
       (
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="orgs-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Events</th><th className="px-4 py-3">Leads</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.contact_email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{r.slug}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-md ${r.status === "active" ? "bg-green-50 text-green-700" : r.status === "suspended" ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-gray-700">{r.events_count}</td>
                  <td className="px-4 py-3 text-gray-700">{r.leads_count}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => nav(`/admin/organizations/${r.id}`)} className="text-purple-700 hover:text-purple-900 text-xs mr-3" data-testid={`open-org-${r.slug}`}>Open →</button>
                    <button onClick={() => del(r.id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-org-${r.slug}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      <Modal open={modal} onClose={() => setModal(false)} title="Onboard new organization">
        <form onSubmit={submit} className="space-y-4" data-testid="new-org-form">
          <Input label="Organization name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="org-name" />
          <Input label="Slug (URL-safe)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required placeholder="e.g. absa-botswana" testId="org-slug" />
          <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={3} testId="org-description" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://" testId="org-website" />
            <Input label="Contact email" value={form.contact_email} type="email" onChange={(v) => setForm({ ...form, contact_email: v })} testId="org-contact-email" />
          </div>
          <Input label="Brand color (hex)" value={form.brand_color} onChange={(v) => setForm({ ...form, brand_color: v })} placeholder="#7C3AED" testId="org-brand-color" />
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500 mb-3">Organization admin account</div>
            <Input label="Admin full name" value={form.admin_full_name} onChange={(v) => setForm({ ...form, admin_full_name: v })} testId="admin-fullname" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Input label="Admin email" type="email" required value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testId="admin-email" />
              <Input label="Temporary password" required value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} testId="admin-password" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving} testId="submit-new-org">{saving ? "Creating…" : "Create organization"}</Btn>
          </div>
        </form>
      </Modal>
    </DashboardShell>
  );
}

/* ==================== Organization detail (billing + AI settings via super admin) ==================== */
function OrgDetail() {
  const { pathname } = window.location;
  const orgId = pathname.split("/").pop();
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/organizations/${orgId}`).then((r) => setOrg(r.data)); }, [orgId]);

  async function save(patch) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/organizations/${orgId}`, patch);
      setOrg(data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  }

  if (!org) return <DashboardShell title="Super Admin" subtitle="Organization" nav={NAV}><div className="text-sm text-gray-500">Loading…</div></DashboardShell>;

  return (
    <DashboardShell title="Super Admin" subtitle={org.name} nav={NAV}>
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Organization</div>
          <Input label="Name" value={org.name} onChange={(v) => setOrg({ ...org, name: v })} testId="edit-name" />
          <Input label="Description" value={org.description} rows={3} onChange={(v) => setOrg({ ...org, description: v })} testId="edit-desc" />
          <Input label="Website" value={org.website} onChange={(v) => setOrg({ ...org, website: v })} testId="edit-website" />
          <Input label="Contact email" value={org.contact_email} onChange={(v) => setOrg({ ...org, contact_email: v })} testId="edit-contact-email" />
          <Btn onClick={() => save({ name: org.name, description: org.description, website: org.website, contact_email: org.contact_email })} disabled={saving} testId="save-org">Save changes</Btn>
        </div>
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Billing (super admin only)</div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Status</label>
            <select value={org.status} onChange={(e) => setOrg({ ...org, status: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="edit-status">
              <option value="active">Active</option><option value="trial">Trial</option><option value="suspended">Suspended</option><option value="inactive">Inactive</option>
            </select>
          </div>
          <Input label="Renewal date" value={org.renewal_date} onChange={(v) => setOrg({ ...org, renewal_date: v })} placeholder="YYYY-MM-DD" testId="edit-renewal" />
          <Input label="Billing notes" value={org.billing_notes} rows={3} onChange={(v) => setOrg({ ...org, billing_notes: v })} testId="edit-billing-notes" />
          <Input label="Internal notes" value={org.internal_notes} rows={3} onChange={(v) => setOrg({ ...org, internal_notes: v })} testId="edit-internal-notes" />
          <Btn onClick={() => save({ status: org.status, renewal_date: org.renewal_date, billing_notes: org.billing_notes, internal_notes: org.internal_notes })} disabled={saving} testId="save-billing">Save billing</Btn>
        </div>
      </div>
    </DashboardShell>
  );
}

/* ==================== Users ==================== */
function UsersPage() {
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", role: "org_admin", org_id: "", full_name: "" });

  async function refresh() {
    const [u, o] = await Promise.all([api.get("/users"), api.get("/organizations")]);
    setRows(u.data);
    setOrgs(o.data);
  }
  useEffect(() => { refresh(); }, []);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setModal(false);
      setForm({ email: "", password: "", role: "org_admin", org_id: "", full_name: "" });
      refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }
  async function del(id) {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    refresh();
  }

  return (
    <DashboardShell title="Super Admin" subtitle="Users" nav={NAV}>
      <SectionTitle title="All users" action={<Btn onClick={() => setModal(true)} testId="new-user-btn"><Plus size={14} /> New user</Btn>} />
      <div className="border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-gray-50 border-b border-gray-200"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold"><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Organization</th><th className="px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((u) => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3"><div className="font-medium text-gray-900">{u.email}</div><div className="text-xs text-gray-500">{u.full_name || "—"}</div></td>
                <td className="px-4 py-3 text-gray-600"><span className={`text-xs px-2 py-1 rounded-md ${u.role === "super_admin" ? "bg-purple-50 text-purple-700" : "bg-gray-100 text-gray-700"}`}>{u.role}</span></td>
                <td className="px-4 py-3 text-gray-600">{orgs.find((o) => o.id === u.org_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(u.id)} className="text-gray-400 hover:text-red-600" data-testid={`delete-user-${u.email}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title="Create user">
        <form onSubmit={create} className="space-y-4">
          <Input label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testId="user-fullname" />
          <Input label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="user-email" />
          <Input label="Password" required value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="user-password" />
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
              <option value="org_admin">Org Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          {form.role === "org_admin" && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Organization</label>
              <select value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} required className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
                <option value="">Select…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn type="submit" testId="submit-new-user">Create</Btn></div>
        </form>
      </Modal>
    </DashboardShell>
  );
}

/* ==================== Demo Requests ==================== */
function DemoRequests() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/demo-requests").then((r) => setRows(r.data)); }, []);
  return (
    <DashboardShell title="Super Admin" subtitle="Demo Requests" nav={NAV}>
      <SectionTitle title="Incoming demo requests" />
      {rows.length === 0 ? <EmptyState icon={<Inbox size={18} />} title="No demo requests yet" body="They'll appear here as visitors fill out the Book a Demo form." /> :
        <div className="border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200"><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold"><th className="px-4 py-3">Name</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Received</th></tr></thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">{r.full_name}</td>
                  <td className="px-4 py-3 text-gray-700">{r.company || "—"}</td>
                  <td className="px-4 py-3 text-gray-700">{r.email}</td>
                  <td className="px-4 py-3 text-gray-700">{r.phone || "—"}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{r.message || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{r.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </DashboardShell>
  );
}
